// AI Coach — 4-phase wizard ported from prototype ai-coach.jsx.
// library → brief → tailor → deliver.
//
// Library: card grid of past coach sessions + a "How it works" trio.
// Brief: structured form with source playbook picker, customer + meeting,
//        attendees, context, and a Generate CTA.
// Tailor: shows the tailored hook + per-beat cards with annotations.
// Deliver: presenter-mode summary card with export actions.
//
// Driven by a local phase state (this is a single-page wizard).

import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { MissionControlHero } from "@/components/hero/MissionControlHero";
import { useUiStore } from "@/lib/store/uiStore";
import { api } from "@/lib/api";
import { aiApi } from "@/lib/api/ai";
import { PLAYBOOK_CONTENT } from "@/lib/data";
import {
  SAMPLE_LIBRARY,
  SAMPLE_TAILORED,
  isCoachFirstVisit,
  markCoachFirstVisitDone,
} from "@/lib/coachSamples";
import type { Playbook } from "@/lib/types";

type Phase = "library" | "brief" | "tailor" | "deliver";

interface Attendee {
  name: string;
  role: string;
}
interface BriefForm {
  sourceId: string;
  customer: string;
  industry: string;
  meetingGoal: string;
  attendees: Attendee[];
  knownPains: string;
  competitors: string;
  notes: string;
}

// Shape returned by /api/ai/demo-coach (see DEMO_COACH_SYSTEM_PROMPT).
interface CoachPlanSegment {
  title: string;
  duration?: string;
  what_to_show?: string;
  what_to_say?: string;
  concierge_question?: string;
}
interface CoachPlanObjection {
  objection: string;
  response: string;
}
interface CoachPlan {
  opening: string;
  segments: CoachPlanSegment[];
  closing: string;
  objections?: CoachPlanObjection[];
}

interface Battlecard {
  competitor: string;
  our_strengths?: string[];
  their_strengths?: string[];
  landmines?: string[];
  positioning?: string;
}

// ─── Local persistence ────────────────────────────────────────────
// Coach sessions are private to each user. We persist them in
// localStorage keyed by the current user (best-effort) so opening the
// app again keeps the saved sessions for that browser/account.
interface SavedSession {
  id: string;
  customer: string;
  industry: string;
  meetingGoal: string;
  attendees: { name: string; role: string }[];
  knownPains: string;
  competitors: string;
  notes: string;
  sourceId: string;
  sourceTitle: string;
  plan: CoachPlan | null;
  status: "ready" | "draft";
  createdAt: number;
  updatedAt: number;
}

const SESSIONS_KEY = "qbranch.coach.sessions.v1";

function loadSessions(): SavedSession[] {
  try {
    const raw = localStorage.getItem(SESSIONS_KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw) as SavedSession[];
    return Array.isArray(arr) ? arr : [];
  } catch {
    return [];
  }
}

function saveSessions(list: SavedSession[]): void {
  try {
    localStorage.setItem(SESSIONS_KEY, JSON.stringify(list));
  } catch {
    // Best-effort. Quota exceeded or private mode — silently ignore.
  }
}

function upsertSession(session: SavedSession): SavedSession[] {
  const list = loadSessions();
  const idx = list.findIndex((s) => s.id === session.id);
  if (idx >= 0) list[idx] = session;
  else list.unshift(session);
  saveSessions(list);
  return list;
}

function deleteSession(id: string): SavedSession[] {
  const list = loadSessions().filter((s) => s.id !== id);
  saveSessions(list);
  return list;
}

function relativeTime(ms: number): string {
  const delta = Date.now() - ms;
  const sec = Math.max(1, Math.floor(delta / 1000));
  if (sec < 60) return `${sec}s ago`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day < 30) return `${day}d ago`;
  return new Date(ms).toLocaleDateString();
}

// SAMPLE_LIBRARY / SAMPLE_TAILORED moved to @/lib/coachSamples so a
// returning user with zero saved sessions can be shown a real empty
// state instead of perpetual fake demo rows.

export function Coach(): JSX.Element {
  const navigate = useNavigate();
  const showToast = useUiStore((s) => s.showToast);
  const [phase, setPhase] = useState<Phase>("library");
  const { data: playbooks = [] } = useQuery({
    queryKey: ["playbooks"],
    queryFn: api.listPlaybooks,
  });

  const [brief, setBrief] = useState<BriefForm>({
    sourceId: "",
    customer: "",
    industry: "",
    meetingGoal: "",
    attendees: [
      { name: "", role: "" },
      { name: "", role: "" },
    ],
    knownPains: "",
    competitors: "",
    notes: "",
  });
  const [plan, setPlan] = useState<CoachPlan | null>(null);
  const [sessions, setSessions] = useState<SavedSession[]>(() => loadSessions());
  const [activeSessionId, setActiveSessionId] = useState<string | null>(null);

  useEffect(() => {
    if (!brief.sourceId && playbooks.length > 0) {
      setBrief((b) => ({ ...b, sourceId: playbooks[0].id }));
    }
  }, [playbooks, brief.sourceId]);

  const newCoach = (): void => {
    setActiveSessionId(null);
    setPlan(null);
    setBrief({
      sourceId: playbooks[0]?.id ?? "",
      customer: "",
      industry: "",
      meetingGoal: "",
      attendees: [
        { name: "", role: "" },
        { name: "", role: "" },
      ],
      knownPains: "",
      competitors: "",
      notes: "",
    });
    setPhase("brief");
  };

  const openSession = (id: string): void => {
    const s = sessions.find((x) => x.id === id);
    if (!s) return;
    setActiveSessionId(id);
    setBrief({
      sourceId: s.sourceId,
      customer: s.customer,
      industry: s.industry,
      meetingGoal: s.meetingGoal,
      attendees: s.attendees.length > 0 ? s.attendees : [{ name: "", role: "" }],
      knownPains: s.knownPains,
      competitors: s.competitors,
      notes: s.notes,
    });
    setPlan(s.plan);
    setPhase(s.plan ? "tailor" : "brief");
  };

  const persistSession = (
    nextPlan: CoachPlan | null,
    status: "ready" | "draft",
  ): string => {
    const id =
      activeSessionId ??
      `coach_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 6)}`;
    const sourceTitle =
      playbooks.find((p) => p.id === brief.sourceId)?.title ?? "Custom";
    const now = Date.now();
    const existing = sessions.find((s) => s.id === id);
    const session: SavedSession = {
      id,
      customer: brief.customer || "Untitled session",
      industry: brief.industry,
      meetingGoal: brief.meetingGoal,
      attendees: brief.attendees,
      knownPains: brief.knownPains,
      competitors: brief.competitors,
      notes: brief.notes,
      sourceId: brief.sourceId,
      sourceTitle,
      plan: nextPlan,
      status,
      createdAt: existing?.createdAt ?? now,
      updatedAt: now,
    };
    const next = upsertSession(session);
    setSessions(next);
    setActiveSessionId(id);
    return id;
  };

  const removeSession = (id: string): void => {
    const next = deleteSession(id);
    setSessions(next);
    if (activeSessionId === id) setActiveSessionId(null);
    showToast("Session removed");
  };

  return (
    <>
      {phase === "library" && (
        <CoachLibrary
          sessions={sessions}
          onNew={newCoach}
          onOpen={openSession}
          onDelete={removeSession}
        />
      )}
      {phase === "brief" && (
        <CoachBrief
          brief={brief}
          setBrief={setBrief}
          playbooks={playbooks}
          onBack={() => {
            // Auto-save a draft before leaving so users don't lose work.
            if (brief.customer.trim() || brief.meetingGoal.trim()) {
              persistSession(plan, plan ? "ready" : "draft");
            }
            setPhase("library");
          }}
          onGenerated={(p) => {
            setPlan(p);
            persistSession(p, p ? "ready" : "draft");
            setPhase("tailor");
          }}
          onPickSource={(id) => setBrief((b) => ({ ...b, sourceId: id }))}
          showToast={showToast}
        />
      )}
      {phase === "tailor" && (
        <CoachTailor
          brief={brief}
          plan={plan}
          playbooks={playbooks}
          onBack={() => setPhase("brief")}
          onDeliver={() => setPhase("deliver")}
          onSave={() => {
            persistSession(plan, plan ? "ready" : "draft");
            showToast("Session saved");
          }}
          showToast={showToast}
        />
      )}
      {phase === "deliver" && (
        <CoachDeliver
          brief={brief}
          plan={plan}
          onBack={() => setPhase("tailor")}
          onLibrary={() => {
            persistSession(plan, plan ? "ready" : "draft");
            setPhase("library");
          }}
          onPlaybooks={() => navigate("/playbooks")}
          showToast={showToast}
        />
      )}
    </>
  );
}

// ─── Library ──────────────────────────────────────────────────────
function CoachLibrary({
  sessions,
  onNew,
  onOpen,
  onDelete,
}: {
  sessions: SavedSession[];
  onNew: () => void;
  onOpen: (id: string) => void;
  onDelete: (id: string) => void;
}): JSX.Element {
  // Show the user's saved sessions first. For a brand-new visitor we
  // render the Halo / Meridian sample rows so the page demos itself.
  // For a returning user who has deleted everything we render a real
  // empty state instead — the audit flagged the previous behavior of
  // perpetually re-showing fake data as confusing.
  const [firstVisit] = useState<boolean>(() => isCoachFirstVisit());
  useEffect(() => {
    // The moment the user has at least one real session, retire the
    // first-visit flag so we never re-show samples.
    if (sessions.length > 0) markCoachFirstVisitDone();
  }, [sessions.length]);

  const showSamples = sessions.length === 0 && firstVisit;
  const showEmptyState = sessions.length === 0 && !firstVisit;
  const lib =
    sessions.length > 0
      ? sessions.map((s) => ({
          id: s.id,
          customer: s.customer,
          industry: s.industry || "—",
          sourcePlaybookTitle: s.sourceTitle,
          meetingDate: new Date(s.updatedAt).toLocaleDateString(undefined, {
            month: "short",
            day: "numeric",
            hour: "numeric",
            minute: "2-digit",
          }),
          attendees: s.attendees.filter((a) => a.name.trim()).length,
          status: s.status,
          updated: relativeTime(s.updatedAt),
          deletable: true as const,
        }))
      : showSamples
        ? SAMPLE_LIBRARY.map((s) => ({ ...s, deletable: false as const }))
        : [];
  return (
    <div style={{ maxWidth: 1080, margin: "0 auto", padding: "28px 24px 120px" }}>
      <MissionControlHero
        breadcrumb="AI Coach"
        title="Your tailored demo coaches"
        subtitle="Customize any playbook to a specific meeting — attendees, competitors, goals, objections"
        iconGlyph="✦"
        astro={{ pose: "happy", message: "I'm your AI agent ✦" }}
        chips={[
          { label: `${lib.length} session${lib.length === 1 ? "" : "s"}`, bg: "rgba(255,255,255,.14)" },
          { label: "Saved on this device", bg: "rgba(255,255,255,.14)" },
        ]}
        rightSlot={<SparkleButton onClick={onNew}>New coach session</SparkleButton>}
      />

      <div
        style={{
          margin: "20px 0 10px",
          display: "flex",
          alignItems: "center",
          gap: 10,
        }}
      >
        <div className="label">Upcoming & recent</div>
        <div style={{ flex: 1, height: 1, background: "var(--bd-2)" }} />
      </div>

      {showEmptyState && (
        <div
          className="card"
          style={{
            padding: "32px 24px",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            textAlign: "center",
            gap: 12,
          }}
        >
          <img
            src="/assets/astro-happy.png"
            alt=""
            style={{ width: 96, height: 96, objectFit: "contain" }}
            onError={(e) => {
              (e.currentTarget as HTMLImageElement).style.display = "none";
            }}
          />
          <div style={{ fontSize: 16, fontWeight: 600, color: "var(--ink)" }}>
            No saved coach sessions yet
          </div>
          <div
            style={{
              fontSize: 12,
              color: "var(--ink-2)",
              maxWidth: 420,
              lineHeight: 1.5,
            }}
          >
            Pick a playbook, brief the AI on your meeting, and your tailored
            coach plan lands here. Sessions are saved on this device only.
          </div>
          <button
            className="btn primary"
            onClick={onNew}
            style={{ marginTop: 6 }}
          >
            ✦ New coach session
          </button>
        </div>
      )}

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(2, 1fr)",
          gap: 12,
        }}
      >
        {lib.map((c) => {
          const chip =
            c.status === "ready"
              ? {
                  bg: "var(--chip-teal-bg)",
                  color: "var(--teal)",
                  label: "Ready",
                }
              : {
                  bg: "var(--chip-yellow-bg)",
                  color: "var(--chip-yellow-fg)",
                  label: "Draft",
                };
          return (
            <button
              key={c.id}
              onClick={() => onOpen(c.id)}
              className="card"
              style={{
                textAlign: "left",
                padding: 18,
                cursor: "pointer",
                display: "flex",
                flexDirection: "column",
                gap: 12,
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  justifyContent: "space-between",
                  gap: 12,
                }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      fontSize: 15,
                      fontWeight: 600,
                      color: "var(--ink)",
                    }}
                  >
                    {c.customer}
                  </div>
                  <div
                    style={{
                      fontSize: 12,
                      color: "var(--ink-3)",
                      marginTop: 3,
                    }}
                  >
                    {c.industry} · {c.attendees} attendees · {c.meetingDate}
                  </div>
                </div>
                <SparkleBadge />
              </div>
              <div
                style={{
                  fontSize: 12,
                  color: "var(--ink-2)",
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                }}
              >
                <span style={{ color: "var(--ink-3)" }}>Source:</span>
                <span style={{ fontWeight: 500 }}>{c.sourcePlaybookTitle}</span>
              </div>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  paddingTop: 10,
                  borderTop: "1px solid var(--bd-2)",
                }}
              >
                <span
                  style={{
                    fontSize: 10,
                    fontWeight: 700,
                    padding: "3px 9px",
                    borderRadius: 999,
                    background: chip.bg,
                    color: chip.color,
                    textTransform: "uppercase",
                    letterSpacing: ".08em",
                  }}
                >
                  {chip.label}
                </span>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ fontSize: 10, color: "var(--ink-3)" }}>
                    Updated {c.updated}
                  </span>
                  {c.deletable && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        if (confirm(`Delete "${c.customer}" session?`)) {
                          onDelete(c.id);
                        }
                      }}
                      style={{
                        background: "transparent",
                        border: "1px solid var(--bd)",
                        borderRadius: 6,
                        fontSize: 10,
                        padding: "2px 8px",
                        color: "var(--ink-3)",
                        cursor: "pointer",
                      }}
                    >
                      Delete
                    </button>
                  )}
                </div>
              </div>
            </button>
          );
        })}
      </div>

      <div
        style={{
          marginTop: 28,
          padding: 18,
          background: "linear-gradient(135deg, #f8f5fe, white 70%)",
          border: "1px solid #e9dcfb",
          borderRadius: 12,
        }}
      >
        <div
          style={{
            fontSize: 10,
            fontWeight: 700,
            color: "var(--violet)",
            textTransform: "uppercase",
            letterSpacing: ".1em",
            marginBottom: 8,
          }}
        >
          How AI Coach works
        </div>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(3, 1fr)",
            gap: 14,
          }}
        >
          {[
            ["1", "Brief", "Pick a playbook + tell AI about your meeting: customer, attendees, goals, objections."],
            ["2", "Tailor", "AI rewrites the script for your specific audience — with annotations on what changed and why."],
            ["3", "Deliver", "Review the final artifact, export to PDF, share to Slack (soon), or save for later."],
          ].map(([n, title, text]) => (
            <div
              key={n}
              style={{
                padding: 12,
                background: "white",
                border: "1px solid var(--bd-2)",
                borderRadius: 8,
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  marginBottom: 6,
                }}
              >
                <div
                  style={{
                    width: 20,
                    height: 20,
                    borderRadius: 5,
                    background:
                      "linear-gradient(135deg, var(--violet), var(--eb-50))",
                    color: "white",
                    fontSize: 11,
                    fontWeight: 700,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {n}
                </div>
                <div
                  style={{
                    fontSize: 12,
                    fontWeight: 600,
                    color: "var(--ink)",
                  }}
                >
                  {title}
                </div>
              </div>
              <div
                style={{ fontSize: 11, color: "var(--ink-2)", lineHeight: 1.5 }}
              >
                {text}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── Brief ────────────────────────────────────────────────────────
function CoachBrief({
  brief,
  setBrief,
  playbooks,
  onBack,
  onGenerated,
  onPickSource,
  showToast,
}: {
  brief: BriefForm;
  setBrief: (
    fn: (b: BriefForm) => BriefForm,
  ) => void;
  playbooks: Playbook[];
  onBack: () => void;
  onGenerated: (plan: CoachPlan | null) => void;
  onPickSource: (id: string) => void;
  showToast: (msg: string) => void;
}): JSX.Element {
  const [generating, setGenerating] = useState(false);

  const set = <K extends keyof BriefForm>(k: K, v: BriefForm[K]): void =>
    setBrief((b) => ({ ...b, [k]: v }));
  const setAtt = (i: number, k: keyof Attendee, v: string): void =>
    setBrief((b) => ({
      ...b,
      attendees: b.attendees.map((a, ai) =>
        ai === i ? { ...a, [k]: v } : a,
      ),
    }));
  const addAtt = (): void =>
    setBrief((b) => ({ ...b, attendees: [...b.attendees, { name: "", role: "" }] }));
  const rmAtt = (i: number): void =>
    setBrief((b) => ({
      ...b,
      attendees: b.attendees.filter((_, ai) => ai !== i),
    }));

  const canGenerate =
    brief.customer.trim() &&
    brief.meetingGoal.trim() &&
    brief.attendees.some((a) => a.name.trim());

  const generate = async (): Promise<void> => {
    if (!canGenerate) return;
    setGenerating(true);
    try {
      const customerContext = [
        `Customer: ${brief.customer}`,
        brief.industry && `Industry: ${brief.industry}`,
        `Meeting goal: ${brief.meetingGoal}`,
        `Attendees:\n${brief.attendees
          .filter((a) => a.name.trim())
          .map((a) => `- ${a.name} (${a.role})`)
          .join("\n")}`,
        brief.knownPains && `Known pains:\n${brief.knownPains}`,
        brief.competitors && `Competitors: ${brief.competitors}`,
        brief.notes && `Notes: ${brief.notes}`,
      ]
        .filter(Boolean)
        .join("\n\n");
      // Use the source playbook's pack id when available, otherwise let
      // the backend default to its own pack picker. Prod's DemoCoach
      // ALWAYS passes pack_id through so the AI grounds its segments in
      // the actual pack's stories/personas; v2 was silently dropping it
      // and getting generic Cirrus output for every pack.
      const sourcePlaybook = playbooks.find((p) => p.id === brief.sourceId);
      const packId = sourcePlaybook?.profileId;
      const plan = (await aiApi.demoCoach(
        customerContext,
        packId,
      )) as CoachPlan | null;
      onGenerated(plan);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Generation failed";
      showToast(`AI Coach failed: ${msg}`);
    } finally {
      setGenerating(false);
    }
  };

  const prefill = (): void => {
    setBrief((b) => ({
      ...b,
      customer: "Halo Power Solutions",
      industry: "Solar / Clean Energy",
      meetingGoal:
        "Win sponsorship for a 90-day pilot — they manage 380+ open opps on spreadsheets and their forecast slipped 22% last quarter.",
      attendees: [
        { name: "Priya Chen", role: "CFO" },
        { name: "Marcus Webb", role: "VP Sales" },
        { name: "Dana Alvarez", role: "Director, RevOps" },
      ],
      knownPains:
        "Forecast accuracy (CFO doesn't trust it)\nRep tenure <1yr avg\nNo signal from 6 BI tools already paid for",
      competitors: "Clari, Tableau (legacy), Excel",
      notes:
        "Priya will push on ROI in year 1 and licensing cost. Marcus cares about seller adoption.",
    }));
    showToast("Pre-filled with Halo Power Solutions example");
  };

  if (generating) return <GeneratingState />;

  const sourceTitle =
    playbooks.find((p) => p.id === brief.sourceId)?.title ?? "—";

  return (
    <div style={{ maxWidth: 880, margin: "0 auto", padding: "28px 24px 120px" }}>
      <MissionControlHero
        breadcrumb="AI Coach"
        modePill="Brief"
        title="New coach session"
        subtitle="Tell AI about the meeting — it will rewrite the playbook for this specific audience"
        iconGlyph="✦"
        rightSlot={
          <button
            onClick={onBack}
            className="btn ghost"
            style={{
              background: "rgba(255,255,255,.12)",
              color: "white",
              border: "1px solid rgba(255,255,255,.25)",
            }}
          >
            ← Back to library
          </button>
        }
      />

      <Section title="Source playbook" number="1">
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(2, 1fr)",
            gap: 8,
          }}
        >
          {playbooks.map((p) => {
            const active = brief.sourceId === p.id;
            return (
              <button
                key={p.id}
                onClick={() => onPickSource(p.id)}
                style={{
                  textAlign: "left",
                  padding: 12,
                  cursor: "pointer",
                  background: active ? "var(--cb-95)" : "white",
                  border: active
                    ? "1.5px solid var(--eb-50)"
                    : "1px solid var(--bd)",
                  borderRadius: 8,
                  display: "flex",
                  gap: 10,
                  alignItems: "flex-start",
                }}
              >
                <div
                  style={{
                    width: 28,
                    height: 28,
                    borderRadius: 6,
                    flexShrink: 0,
                    background: active ? "var(--eb-50)" : "var(--cb-95)",
                    color: active ? "white" : "var(--eb-50)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 14,
                  }}
                >
                  ▤
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      fontSize: 13,
                      fontWeight: 600,
                      color: "var(--ink)",
                    }}
                  >
                    {p.title}
                  </div>
                  <div
                    style={{
                      fontSize: 11,
                      color: "var(--ink-3)",
                      marginTop: 2,
                    }}
                  >
                    {p.audience.join(" · ")} · {p.length}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </Section>

      <Section
        title="Who are you meeting with?"
        number="2"
        rightSlot={
          <button
            onClick={prefill}
            className="btn ghost"
            style={{ fontSize: 11, padding: "4px 10px" }}
          >
            ✦ Use example data
          </button>
        }
      >
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "2fr 1fr",
            gap: 10,
            marginBottom: 10,
          }}
        >
          <Field label="Customer / company">
            <input
              value={brief.customer}
              onChange={(e) => set("customer", e.target.value)}
              placeholder="Halo Power Solutions"
              style={fieldStyle}
            />
          </Field>
          <Field label="Industry">
            <input
              value={brief.industry}
              onChange={(e) => set("industry", e.target.value)}
              placeholder="Solar / Clean Energy"
              style={fieldStyle}
            />
          </Field>
        </div>
        <Field label="Meeting goal">
          <textarea
            value={brief.meetingGoal}
            onChange={(e) => set("meetingGoal", e.target.value)}
            rows={2}
            placeholder="What outcome do you want from this meeting?"
            style={{ ...fieldStyle, resize: "vertical" }}
          />
        </Field>
      </Section>

      <Section
        title="Attendees"
        number="3"
        subtitle="Name + role. AI adapts the script to each person's lens."
      >
        <div style={{ display: "grid", gap: 8 }}>
          {brief.attendees.map((a, i) => (
            <div
              key={i}
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr 28px",
                gap: 8,
                alignItems: "center",
              }}
            >
              <input
                value={a.name}
                onChange={(e) => setAtt(i, "name", e.target.value)}
                placeholder="Name"
                style={fieldStyle}
              />
              <input
                value={a.role}
                onChange={(e) => setAtt(i, "role", e.target.value)}
                placeholder="Title / role (CFO, VP Sales…)"
                style={fieldStyle}
              />
              <button
                onClick={() => rmAtt(i)}
                style={{
                  background: "transparent",
                  border: "1px solid var(--bd)",
                  borderRadius: 6,
                  width: 28,
                  height: 32,
                  cursor: "pointer",
                  color: "var(--ink-3)",
                }}
                disabled={brief.attendees.length <= 1}
              >
                ×
              </button>
            </div>
          ))}
        </div>
        <button
          onClick={addAtt}
          style={{
            marginTop: 10,
            padding: "6px 12px",
            fontSize: 12,
            background: "transparent",
            border: "1px dashed var(--bd)",
            borderRadius: 6,
            cursor: "pointer",
            color: "var(--ink-2)",
          }}
        >
          + Add attendee
        </button>
      </Section>

      <Section
        title="Context — what does AI need to know?"
        number="4"
        subtitle="Pains, competitors, anything specific to this meeting."
      >
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 10,
            marginBottom: 10,
          }}
        >
          <Field label="Known pain points">
            <textarea
              value={brief.knownPains}
              onChange={(e) => set("knownPains", e.target.value)}
              rows={4}
              placeholder="One per line"
              style={{ ...fieldStyle, resize: "vertical" }}
            />
          </Field>
          <Field label="Competitors in play">
            <textarea
              value={brief.competitors}
              onChange={(e) => set("competitors", e.target.value)}
              rows={4}
              placeholder="Clari, Tableau, Excel…"
              style={{ ...fieldStyle, resize: "vertical" }}
            />
          </Field>
        </div>
        <Field label="Additional notes (optional)">
          <textarea
            value={brief.notes}
            onChange={(e) => set("notes", e.target.value)}
            rows={2}
            placeholder="Anything else — discovery notes, relationship history, quirks…"
            style={{ ...fieldStyle, resize: "vertical" }}
          />
        </Field>
      </Section>

      <div
        style={{
          marginTop: 24,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 12,
          padding: 18,
          background: "var(--cb-95)",
          border: "1px solid var(--cb-90)",
          borderRadius: 12,
        }}
      >
        <div style={{ fontSize: 12, color: "var(--ink-2)" }}>
          AI will tailor{" "}
          <strong style={{ color: "var(--ink)" }}>{sourceTitle}</strong>
          {brief.customer && (
            <>
              {" "}for{" "}
              <strong style={{ color: "var(--ink)" }}>{brief.customer}</strong>
            </>
          )}{" "}
          · rewrites stories, objection handling, and closing line for this
          specific audience.
        </div>
        <button
          onClick={() => { void generate(); }}
          disabled={!canGenerate}
          className="btn primary lg"
          style={{
            opacity: canGenerate ? 1 : 0.5,
            whiteSpace: "nowrap",
            display: "inline-flex",
            alignItems: "center",
            gap: 8,
          }}
        >
          <span style={{ fontSize: 14 }}>✦</span> Generate coach session
        </button>
      </div>
    </div>
  );
}

function GeneratingState(): JSX.Element {
  const steps = [
    "Reading source playbook…",
    "Analyzing attendees and their lens…",
    "Mapping objections to your competitors…",
    "Rewriting story beats for this audience…",
    "Drafting landing line and next steps…",
  ];
  const [active, setActive] = useState(0);
  useEffect(() => {
    const id = setInterval(
      () => setActive((a) => Math.min(steps.length, a + 1)),
      320,
    );
    return () => clearInterval(id);
  }, []);

  return (
    <div
      style={{
        maxWidth: 520,
        margin: "80px auto",
        padding: "40px 24px",
        textAlign: "center",
      }}
    >
      <div
        style={{
          width: 72,
          height: 72,
          borderRadius: 18,
          margin: "0 auto 18px",
          background: "linear-gradient(135deg, var(--violet), var(--eb-50))",
          color: "white",
          fontSize: 36,
          fontWeight: 800,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          animation: "coachPulse 1.2s ease-in-out infinite",
        }}
      >
        ✦
      </div>
      <style>{`
        @keyframes coachPulse {
          0%, 100% { transform: scale(1); opacity: .9; }
          50%      { transform: scale(1.06); opacity: 1; }
        }
      `}</style>
      <div
        style={{
          fontSize: 16,
          fontWeight: 600,
          color: "var(--ink)",
          marginBottom: 4,
        }}
      >
        Tailoring your demo
      </div>
      <div
        style={{
          fontSize: 12,
          color: "var(--ink-3)",
          marginBottom: 22,
        }}
      >
        This typically takes 20–30 seconds
      </div>
      <div style={{ display: "grid", gap: 8, textAlign: "left" }}>
        {steps.map((s, i) => {
          const done = i < active;
          const on = i === active;
          return (
            <div
              key={i}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                padding: "8px 12px",
                background: on ? "var(--cb-95)" : "white",
                border: "1px solid var(--bd-2)",
                borderRadius: 8,
              }}
            >
              <div
                style={{
                  width: 18,
                  height: 18,
                  borderRadius: 999,
                  background: done
                    ? "var(--teal)"
                    : on
                      ? "var(--violet)"
                      : "var(--bd)",
                  color: "white",
                  fontSize: 10,
                  fontWeight: 800,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {done ? "✓" : on ? "✦" : ""}
              </div>
              <div
                style={{
                  fontSize: 12,
                  color: done ? "var(--ink-3)" : "var(--ink)",
                }}
              >
                {s}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Tailor ───────────────────────────────────────────────────────
function CoachTailor({
  brief,
  plan,
  playbooks,
  onBack,
  onDeliver,
  onSave,
  showToast,
}: {
  brief: BriefForm;
  plan: CoachPlan | null;
  playbooks: Playbook[];
  onBack: () => void;
  onDeliver: () => void;
  onSave: () => void;
  showToast: (msg: string) => void;
}): JSX.Element {
  const [showAnnotations, setShowAnnotations] = useState(true);
  const sourcePlaybook = useMemo(
    () => playbooks.find((p) => p.id === brief.sourceId),
    [playbooks, brief.sourceId],
  );
  const sourceTitle = sourcePlaybook?.title ?? "Playbook";
  const packId = sourcePlaybook?.profileId;
  const conciergeQuestions = useMemo(
    () =>
      (plan?.segments ?? [])
        .map((s) => s.concierge_question)
        .filter((q): q is string => !!q && q.trim().length > 0),
    [plan?.segments],
  );

  // When the backend has produced a real plan, project it onto the
  // existing tailored UX shape (hook/beats/landing). Otherwise fall back
  // to the Halo sample — used when the user opens an in-memory library
  // entry without invoking the AI. Concierge questions stay on the beat
  // object so the UI can render them as their own copy-to-clipboard pill
  // (mirrors prod's DemoCoach behavior).
  const tailored = useMemo(() => {
    if (!plan) {
      return {
        hook: SAMPLE_TAILORED.hook,
        beats: SAMPLE_TAILORED.beats.map((b) => ({ ...b, concierge: "" })),
        landing: SAMPLE_TAILORED.landing,
      };
    }
    return {
      hook: plan.opening || SAMPLE_TAILORED.hook,
      beats: (plan.segments ?? []).map((s, i) => ({
        id: `seg_${i}`,
        title: s.title || `Segment ${i + 1}`,
        original: s.what_to_show || "—",
        tailored: s.what_to_say || "",
        concierge: s.concierge_question || "",
        annotation: s.duration ? `~${s.duration}` : "",
      })),
      landing: plan.closing || SAMPLE_TAILORED.landing,
    };
  }, [plan]);
  const attendeesLine =
    brief.attendees.filter((a) => a.name).map((a) => `${a.name} (${a.role})`).join(", ") ||
    "Priya Chen (CFO), Marcus Webb (VP Sales), Dana Alvarez (Director, RevOps)";

  return (
    <div style={{ maxWidth: 1080, margin: "0 auto", padding: "28px 24px 120px" }}>
      <MissionControlHero
        breadcrumb="AI Coach"
        modePill="Tailor"
        iconGlyph="✦"
        title={brief.customer || "Halo Power Solutions"}
        subtitle={`Tailored from "${sourceTitle}" · ${brief.industry || "Solar / Clean Energy"}`}
        chips={[
          { label: `${brief.attendees.filter((a) => a.name).length || 3} attendees`, bg: "rgba(255,255,255,.14)" },
          { label: "AI annotated", bg: "rgba(75,192,118,.25)", color: "#A8E5BD", dot: "#4BC076" },
        ]}
        rightSlot={
          <div style={{ display: "flex", gap: 8 }}>
            <button
              onClick={onBack}
              className="btn ghost"
              style={{
                background: "rgba(255,255,255,.12)",
                color: "white",
                border: "1px solid rgba(255,255,255,.25)",
              }}
            >
              ← Back to brief
            </button>
            <button
              onClick={onSave}
              className="btn ghost"
              style={{
                background: "rgba(255,255,255,.12)",
                color: "white",
                border: "1px solid rgba(255,255,255,.25)",
              }}
            >
              Save session
            </button>
            <button
              onClick={onDeliver}
              className="btn primary lg"
              style={{
                background: "#fff",
                color: "var(--eb-15)",
                border: "none",
              }}
            >
              Review & export →
            </button>
          </div>
        }
      />

      {/* Meeting card */}
      <div
        className="card"
        style={{
          marginTop: 16,
          padding: 16,
          display: "grid",
          gridTemplateColumns: "auto 1fr",
          gap: 16,
        }}
      >
        <div
          style={{
            width: 48,
            height: 48,
            borderRadius: 10,
            background: "linear-gradient(135deg, var(--violet), var(--eb-50))",
            color: "white",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 20,
            fontWeight: 800,
            flexShrink: 0,
          }}
        >
          ✦
        </div>
        <div style={{ minWidth: 0 }}>
          <div
            style={{
              fontSize: 11,
              color: "var(--ink-3)",
              textTransform: "uppercase",
              letterSpacing: ".08em",
              fontWeight: 700,
            }}
          >
            Meeting
          </div>
          <div
            style={{
              fontSize: 13,
              color: "var(--ink)",
              marginTop: 4,
              lineHeight: 1.5,
            }}
          >
            {brief.meetingGoal ||
              "Win sponsorship for a 90-day pilot — they manage 380+ open opps on spreadsheets and their forecast slipped 22% last quarter."}
          </div>
          <div
            style={{
              fontSize: 11,
              color: "var(--ink-2)",
              marginTop: 8,
            }}
          >
            <strong style={{ color: "var(--ink-3)" }}>Attendees ·</strong>{" "}
            {attendeesLine}
          </div>
          {brief.competitors && (
            <div
              style={{
                fontSize: 11,
                color: "var(--ink-2)",
                marginTop: 4,
              }}
            >
              <strong style={{ color: "var(--ink-3)" }}>Competitors ·</strong>{" "}
              {brief.competitors}
            </div>
          )}
        </div>
      </div>

      <div
        style={{
          margin: "20px 0 12px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <div className="label">Tailored script</div>
        <label
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            fontSize: 12,
            color: "var(--ink-2)",
            cursor: "pointer",
          }}
        >
          <input
            type="checkbox"
            checked={showAnnotations}
            onChange={(e) => setShowAnnotations(e.target.checked)}
          />
          Show AI tailoring notes
        </label>
      </div>

      <ArtifactBlock label="Opening hook" tone="violet">
        <div
          style={{
            fontSize: 14,
            color: "var(--ink)",
            lineHeight: 1.65,
            fontStyle: "italic",
          }}
        >
          "{tailored.hook}"
        </div>
      </ArtifactBlock>

      <div style={{ display: "grid", gap: 12, marginBottom: 18 }}>
        {tailored.beats.map((b, i) => (
          <BeatCard
            key={b.id}
            index={i + 1}
            title={b.title}
            original={b.original}
            tailored={b.tailored}
            concierge={b.concierge}
            annotation={b.annotation}
            showAnnotation={showAnnotations}
            onCopyConcierge={(text) => {
              void navigator.clipboard?.writeText(text);
              showToast("Concierge prompt copied");
            }}
          />
        ))}
      </div>

      {conciergeQuestions.length > 0 && (
        <div
          className="card"
          style={{
            padding: 14,
            marginBottom: 18,
            background: "#fff8e0",
            border: "1px solid #f5e3a8",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginBottom: 8,
            }}
          >
            <div
              style={{
                fontSize: 11,
                fontWeight: 700,
                color: "#8a5200",
                textTransform: "uppercase",
                letterSpacing: ".08em",
              }}
            >
              Concierge cheat sheet
            </div>
            <button
              onClick={() => {
                const block = conciergeQuestions
                  .map((q, i) => `${i + 1}. ${q}`)
                  .join("\n");
                void navigator.clipboard?.writeText(block);
                showToast("All concierge prompts copied");
              }}
              className="btn ghost sm"
            >
              Copy all
            </button>
          </div>
          <ul
            style={{
              margin: 0,
              padding: "0 0 0 18px",
              fontSize: 12,
              color: "var(--ink-2)",
              lineHeight: 1.6,
            }}
          >
            {conciergeQuestions.map((q, i) => (
              <li key={i}>"{q}"</li>
            ))}
          </ul>
        </div>
      )}

      {plan?.objections && plan.objections.length > 0 && (
        <div className="card" style={{ padding: 16, marginBottom: 18 }}>
          <div
            style={{
              fontSize: 11,
              fontWeight: 700,
              color: "var(--pink)",
              textTransform: "uppercase",
              letterSpacing: ".08em",
              marginBottom: 10,
            }}
          >
            Anticipated objections
          </div>
          <div style={{ display: "grid", gap: 12 }}>
            {plan.objections.map((o, i) => (
              <div key={i}>
                <div style={{ fontSize: 13, fontWeight: 600, color: "var(--ink)" }}>
                  "{o.objection}"
                </div>
                <div
                  style={{
                    fontSize: 12,
                    color: "var(--ink-2)",
                    marginTop: 4,
                    lineHeight: 1.55,
                  }}
                >
                  → {o.response}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <ArtifactBlock label="Landing line" tone="teal">
        <div
          style={{
            fontSize: 14,
            color: "var(--ink)",
            lineHeight: 1.65,
            fontStyle: "italic",
          }}
        >
          "{tailored.landing}"
        </div>
      </ArtifactBlock>

      <BattlecardSection packId={packId} showToast={showToast} />
    </div>
  );
}

// ─── Battlecard ───────────────────────────────────────────────────
// Lightweight inline competitive card for the Tailor view. Mirrors
// prod's `BattlecardSection.jsx`: chips for known competitors, plus a
// freeform input so the user can ask for any vendor on the call.
function BattlecardSection({
  packId,
  showToast,
}: {
  packId: string | undefined;
  showToast: (msg: string) => void;
}): JSX.Element {
  const [competitor, setCompetitor] = useState("");
  const [card, setCard] = useState<Battlecard | null>(null);
  const [loading, setLoading] = useState(false);

  const COMPETITORS = ["Power BI", "Looker", "Domo", "Qlik", "ThoughtSpot", "Sigma"];

  const generate = async (comp: string): Promise<void> => {
    if (!comp.trim()) return;
    setLoading(true);
    setCard(null);
    try {
      const res = (await aiApi.battlecard(comp.trim(), "", packId)) as Battlecard | null;
      if (!res) {
        showToast("Battlecard generation returned no result");
      } else {
        setCard({ ...res, competitor: comp.trim() });
      }
    } catch (e) {
      showToast(`Battlecard failed: ${(e as Error).message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="card"
      style={{
        marginTop: 18,
        padding: 18,
        background: "linear-gradient(135deg, #f0f4ff, white 60%)",
        border: "1px solid #d0d9f6",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 10,
          marginBottom: 12,
        }}
      >
        <div>
          <div
            style={{
              fontSize: 11,
              fontWeight: 700,
              color: "var(--violet)",
              textTransform: "uppercase",
              letterSpacing: ".08em",
            }}
          >
            Competitive battlecard
          </div>
          <div style={{ fontSize: 12, color: "var(--ink-2)", marginTop: 4 }}>
            Pick a competitor — AI generates strengths, landmines, and counter-positioning grounded in the pack.
          </div>
        </div>
      </div>
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 10 }}>
        {COMPETITORS.map((c) => (
          <button
            key={c}
            onClick={() => { void generate(c); }}
            disabled={loading}
            className="btn ghost sm"
          >
            vs {c}
          </button>
        ))}
      </div>
      <div style={{ display: "flex", gap: 8 }}>
        <input
          value={competitor}
          onChange={(e) => setCompetitor(e.target.value)}
          placeholder="Or type any vendor on the call…"
          style={{ ...fieldStyle, flex: 1 }}
        />
        <button
          onClick={() => { void generate(competitor); }}
          disabled={!competitor.trim() || loading}
          className="btn primary sm"
        >
          {loading ? "…" : "Generate"}
        </button>
      </div>

      {loading && (
        <div style={{ marginTop: 12, fontSize: 12, color: "var(--violet)" }}>
          ✦ Generating battlecard…
        </div>
      )}

      {card && (
        <div style={{ marginTop: 14, display: "grid", gap: 10 }}>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 10,
            }}
          >
            <div
              style={{
                padding: 12,
                background: "#ebf5f0",
                border: "1px solid #c0e0d2",
                borderRadius: 8,
              }}
            >
              <div
                style={{
                  fontSize: 10,
                  fontWeight: 700,
                  color: "var(--teal)",
                  textTransform: "uppercase",
                  letterSpacing: ".08em",
                  marginBottom: 6,
                }}
              >
                Our strengths
              </div>
              <ul style={listStyle}>
                {(card.our_strengths ?? []).map((s, i) => (
                  <li key={i}>✓ {s}</li>
                ))}
              </ul>
            </div>
            <div
              style={{
                padding: 12,
                background: "#fdecf0",
                border: "1px solid #f5c5d4",
                borderRadius: 8,
              }}
            >
              <div
                style={{
                  fontSize: 10,
                  fontWeight: 700,
                  color: "var(--pink)",
                  textTransform: "uppercase",
                  letterSpacing: ".08em",
                  marginBottom: 6,
                }}
              >
                Their strengths
              </div>
              <ul style={listStyle}>
                {(card.their_strengths ?? []).map((s, i) => (
                  <li key={i}>• {s}</li>
                ))}
              </ul>
            </div>
          </div>
          {(card.landmines ?? []).length > 0 && (
            <div
              style={{
                padding: 12,
                background: "#fff8e0",
                border: "1px solid #f5e3a8",
                borderRadius: 8,
              }}
            >
              <div
                style={{
                  fontSize: 10,
                  fontWeight: 700,
                  color: "#8a5200",
                  textTransform: "uppercase",
                  letterSpacing: ".08em",
                  marginBottom: 6,
                }}
              >
                Landmine questions to ask
              </div>
              <ul style={listStyle}>
                {(card.landmines ?? []).map((q, i) => (
                  <li key={i}>"{q}"</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const listStyle: React.CSSProperties = {
  margin: 0,
  padding: "0 0 0 16px",
  fontSize: 12,
  color: "var(--ink-2)",
  lineHeight: 1.6,
};

function BeatCard({
  index,
  title,
  original,
  tailored,
  concierge,
  annotation,
  showAnnotation,
  onCopyConcierge,
}: {
  index: number;
  title: string;
  original: string;
  tailored: string;
  concierge?: string;
  annotation: string;
  showAnnotation: boolean;
  onCopyConcierge?: (text: string) => void;
}): JSX.Element {
  return (
    <div className="card" style={{ padding: 16, display: "grid", gap: 10 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <div
          style={{
            width: 26,
            height: 26,
            borderRadius: 7,
            background: "linear-gradient(135deg, var(--violet), var(--eb-50))",
            color: "white",
            fontSize: 12,
            fontWeight: 700,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {index}
        </div>
        <div style={{ fontSize: 13, fontWeight: 600, color: "var(--ink)" }}>
          {title}
        </div>
      </div>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 10,
        }}
      >
        <div
          style={{
            padding: 12,
            background: "var(--cb-98)",
            border: "1px solid var(--bd-2)",
            borderRadius: 8,
          }}
        >
          <div
            style={{
              fontSize: 10,
              fontWeight: 700,
              color: "var(--ink-3)",
              textTransform: "uppercase",
              letterSpacing: ".08em",
              marginBottom: 6,
            }}
          >
            Original
          </div>
          <div
            style={{ fontSize: 12, color: "var(--ink-2)", lineHeight: 1.5 }}
          >
            {original}
          </div>
        </div>
        <div
          style={{
            padding: 12,
            background: "#f0f7fc",
            border: "1px solid #cce3f6",
            borderRadius: 8,
          }}
        >
          <div
            style={{
              fontSize: 10,
              fontWeight: 700,
              color: "var(--eb-50)",
              textTransform: "uppercase",
              letterSpacing: ".08em",
              marginBottom: 6,
            }}
          >
            Tailored
          </div>
          <div
            style={{ fontSize: 12, color: "var(--ink)", lineHeight: 1.5 }}
          >
            {tailored}
          </div>
        </div>
      </div>
      {concierge && (
        <button
          onClick={() => onCopyConcierge?.(concierge)}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            padding: "8px 12px",
            background: "#fff8e0",
            border: "1px solid #f5e3a8",
            borderRadius: 8,
            fontSize: 11,
            color: "#8a5200",
            cursor: "pointer",
            textAlign: "left",
            lineHeight: 1.5,
          }}
          title="Click to copy"
        >
          <span style={{ fontSize: 13 }}>💬</span>
          <span style={{ flex: 1 }}>
            <strong>Concierge prompt:</strong> "{concierge}"
          </span>
          <span style={{ fontSize: 10, opacity: 0.7 }}>copy</span>
        </button>
      )}
      {showAnnotation && annotation && (
        <div
          style={{
            padding: "8px 12px",
            background: "#f8f5fe",
            border: "1px dashed #e9dcfb",
            borderRadius: 8,
            display: "flex",
            gap: 8,
            alignItems: "flex-start",
          }}
        >
          <span style={{ color: "var(--violet)", fontSize: 12 }}>✦</span>
          <div
            style={{ fontSize: 11, color: "var(--ink-2)", lineHeight: 1.5 }}
          >
            <strong style={{ color: "var(--violet)" }}>Why:</strong> {annotation}
          </div>
        </div>
      )}
    </div>
  );
}

function ArtifactBlock({
  label,
  tone,
  children,
}: {
  label: string;
  tone: "violet" | "teal";
  children: React.ReactNode;
}): JSX.Element {
  const TONES = {
    violet: { bg: "#f8f5fe", bd: "#e9dcfb", fg: "var(--violet)" },
    teal:   { bg: "#ebf5f0", bd: "#c0e0d2", fg: "var(--teal)" },
  } as const;
  const t = TONES[tone];
  return (
    <div
      style={{
        padding: 16,
        background: t.bg,
        border: `1px solid ${t.bd}`,
        borderRadius: 12,
        marginBottom: 14,
      }}
    >
      <div
        style={{
          fontSize: 10,
          fontWeight: 700,
          color: t.fg,
          textTransform: "uppercase",
          letterSpacing: ".08em",
          marginBottom: 10,
        }}
      >
        {label}
      </div>
      {children}
    </div>
  );
}

// ─── Deliver ──────────────────────────────────────────────────────
function CoachDeliver({
  brief,
  plan,
  onBack,
  onLibrary,
  onPlaybooks,
  showToast,
}: {
  brief: BriefForm;
  plan: CoachPlan | null;
  onBack: () => void;
  onLibrary: () => void;
  onPlaybooks: () => void;
  showToast: (msg: string) => void;
}): JSX.Element {
  const customer = brief.customer || "Halo Power Solutions";

  // Render the entire plan as plain text — same shape prod's DemoCoach
  // copy uses, so the artifact is portable into Slack, Quip, email,
  // anywhere the seller wants to land it.
  const planText = useMemo(() => {
    if (!plan) return "";
    const segments = (plan.segments ?? [])
      .map(
        (s, i) =>
          `${i + 1}. ${s.title}${s.duration ? ` (${s.duration})` : ""}\n` +
          `   Show: ${s.what_to_show ?? ""}\n` +
          `   Say:  "${s.what_to_say ?? ""}"` +
          (s.concierge_question ? `\n   Concierge: "${s.concierge_question}"` : ""),
      )
      .join("\n\n");
    const objections = (plan.objections ?? [])
      .map((o) => `• "${o.objection}" → ${o.response}`)
      .join("\n");
    return [
      `Demo plan — ${customer}`,
      brief.meetingGoal ? `Goal: ${brief.meetingGoal}` : "",
      "",
      "OPENING",
      plan.opening ?? "",
      "",
      "DEMO FLOW",
      segments,
      "",
      "CLOSING",
      plan.closing ?? "",
      objections ? `\nOBJECTIONS\n${objections}` : "",
    ]
      .filter(Boolean)
      .join("\n");
  }, [plan, customer, brief.meetingGoal]);

  // When we have a real plan, summarize off it; otherwise fall back to
  // the curated PLAYBOOK_CONTENT preview so the page still demos cleanly.
  const stories = plan?.segments?.length ?? PLAYBOOK_CONTENT.stories.length;
  const totalMin = plan
    ? plan.segments.reduce((sum, s) => sum + (parseInt(s.duration ?? "5", 10) || 5), 0) || 30
    : parseInt(PLAYBOOK_CONTENT.intro.duration, 10) +
      PLAYBOOK_CONTENT.stories.reduce(
        (sum, s) => sum + parseInt(s.duration, 10),
        0,
      ) +
      parseInt(PLAYBOOK_CONTENT.qa.duration, 10) +
      parseInt(PLAYBOOK_CONTENT.close.duration, 10);

  return (
    <div style={{ maxWidth: 880, margin: "0 auto", padding: "28px 24px 120px" }}>
      <MissionControlHero
        breadcrumb="AI Coach"
        modePill="Deliver"
        iconGlyph="▶"
        title={`Deliver: ${customer}`}
        subtitle="Open in presenter mode, export, or save the tailored coach for next time"
        rightSlot={
          <div style={{ display: "flex", gap: 8 }}>
            <button
              onClick={onBack}
              className="btn ghost"
              style={{
                background: "rgba(255,255,255,.12)",
                color: "white",
                border: "1px solid rgba(255,255,255,.25)",
              }}
            >
              ← Back to tailor
            </button>
          </div>
        }
      />

      <div
        style={{
          marginTop: 16,
          padding: 22,
          borderRadius: 14,
          background:
            "linear-gradient(135deg, var(--eb-15) 0%, var(--violet) 95%)",
          color: "white",
          boxShadow: "0 8px 32px -12px rgba(107,71,211,.45)",
        }}
      >
        <div
          style={{
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: ".1em",
            textTransform: "uppercase",
            color: "rgba(255,255,255,.8)",
          }}
        >
          Ready to deliver
        </div>
        <div
          style={{
            fontSize: 26,
            fontWeight: 700,
            marginTop: 6,
            letterSpacing: "-0.01em",
          }}
        >
          {customer}
        </div>
        <div
          style={{ fontSize: 13, color: "rgba(255,255,255,.85)", marginTop: 4 }}
        >
          {stories} tailored beats · ~{totalMin} min total
        </div>
        <div style={{ marginTop: 18, display: "flex", gap: 10, flexWrap: "wrap" }}>
          <button
            className="btn primary lg"
            style={{ background: "#fff", color: "var(--eb-15)", border: "none" }}
            onClick={() => {
              if (!planText) {
                showToast("No plan to copy yet — generate one first.");
                return;
              }
              void navigator.clipboard?.writeText(planText);
              showToast("Demo plan copied to clipboard");
            }}
            disabled={!planText}
          >
            📋 Copy full plan
          </button>
          <button
            className="btn ghost"
            style={{
              background: "rgba(255,255,255,.12)",
              color: "white",
              border: "1px solid rgba(255,255,255,.25)",
            }}
            onClick={() => {
              if (!planText) {
                showToast("Generate a plan first.");
                return;
              }
              const subject = encodeURIComponent(`Demo plan — ${customer}`);
              const body = encodeURIComponent(planText);
              window.location.href = `mailto:?subject=${subject}&body=${body}`;
            }}
            disabled={!planText}
          >
            ✉ Email plan
          </button>
          <button
            className="btn ghost"
            style={{
              background: "rgba(255,255,255,.12)",
              color: "white",
              border: "1px solid rgba(255,255,255,.25)",
            }}
            onClick={onLibrary}
          >
            Save & return to library
          </button>
        </div>
      </div>

      <div
        className="card"
        style={{
          marginTop: 18,
          padding: 16,
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 14,
        }}
      >
        <div>
          <div className="label" style={{ marginBottom: 8 }}>
            Open the source playbook
          </div>
          <p style={{ fontSize: 12, color: "var(--ink-2)", lineHeight: 1.6, margin: 0 }}>
            The base script lives in your playbook library. Edits here don't
            change the original — your tailoring stays attached to this coach
            session.
          </p>
          <button
            className="btn primary sm"
            style={{ marginTop: 10 }}
            onClick={onPlaybooks}
          >
            Open playbook →
          </button>
        </div>
        <div>
          <div className="label" style={{ marginBottom: 8 }}>
            What changed
          </div>
          <ul
            style={{
              margin: 0,
              padding: "0 0 0 18px",
              fontSize: 12,
              color: "var(--ink-2)",
              lineHeight: 1.7,
            }}
          >
            <li>Hook personalized to attendees</li>
            <li>Beats reordered for {customer}'s stack</li>
            <li>Objections mapped to {brief.competitors || "competitors"}</li>
            <li>Landing line tied to your meeting goal</li>
          </ul>
        </div>
      </div>
    </div>
  );
}

// ─── Bits ─────────────────────────────────────────────────────────
function Section({
  title,
  number,
  subtitle,
  rightSlot,
  children,
}: {
  title: string;
  number: string;
  subtitle?: string;
  rightSlot?: JSX.Element;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <section style={{ marginTop: 22 }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          marginBottom: 12,
        }}
      >
        <div
          style={{
            width: 24,
            height: 24,
            borderRadius: 6,
            background: "linear-gradient(135deg, var(--violet), var(--eb-50))",
            color: "white",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 12,
            fontWeight: 800,
          }}
        >
          {number}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{ fontSize: 14, fontWeight: 600, color: "var(--ink)" }}
          >
            {title}
          </div>
          {subtitle && (
            <div
              style={{ fontSize: 11, color: "var(--ink-3)", marginTop: 2 }}
            >
              {subtitle}
            </div>
          )}
        </div>
        {rightSlot}
      </div>
      <div className="card" style={{ padding: 16 }}>
        {children}
      </div>
    </section>
  );
}

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <label style={{ display: "block" }}>
      <div
        style={{
          fontSize: 10,
          fontWeight: 700,
          color: "var(--ink-3)",
          textTransform: "uppercase",
          letterSpacing: ".08em",
          marginBottom: 6,
        }}
      >
        {label}
      </div>
      {children}
    </label>
  );
}

const fieldStyle: React.CSSProperties = {
  width: "100%",
  padding: "8px 10px",
  border: "1px solid var(--bd)",
  borderRadius: 8,
  fontSize: 12,
  outline: "none",
  background: "white",
  color: "var(--ink)",
  fontFamily: "inherit",
};

function SparkleButton({
  onClick,
  children,
}: {
  onClick: () => void;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <button
      onClick={onClick}
      className="btn primary lg"
      style={{
        background: "linear-gradient(135deg, #fff 0%, #F6FAFF 100%)",
        color: "var(--eb-15)",
        border: "none",
        whiteSpace: "nowrap",
        display: "inline-flex",
        alignItems: "center",
        gap: 8,
        boxShadow:
          "0 2px 10px rgba(29,112,201,.25), inset 0 0 0 1px rgba(29,112,201,.15)",
      }}
    >
      <span
        style={{
          width: 20,
          height: 20,
          borderRadius: 5,
          background:
            "linear-gradient(135deg, var(--eb-50), var(--violet))",
          color: "white",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 12,
          fontWeight: 800,
        }}
      >
        ✦
      </span>
      {children}
    </button>
  );
}

function SparkleBadge(): JSX.Element {
  return (
    <div
      style={{
        width: 36,
        height: 36,
        borderRadius: 8,
        flexShrink: 0,
        background: "linear-gradient(135deg, var(--violet), var(--eb-50))",
        color: "white",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: 16,
        fontWeight: 800,
      }}
    >
      ✦
    </div>
  );
}
